import { zodResolver } from '@hookform/resolvers/zod'
import { memo, type ReactNode, useEffect } from 'react'
import { type UseFormReturn, useForm } from 'react-hook-form'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Form, FormField } from '@/components/ui/form'
import { Switch } from '@/components/ui/switch'
import { cn } from '@/lib/utils'

import type { BillingAutoTopUpSettings } from './billing.interface'
import {
  type BillingAutoTopUpFormSchemaType,
  billingAutoTopUpFormSchema,
  MAX_AUTO_TOP_UP_AMOUNT,
  MIN_AUTO_TOP_UP_AMOUNT,
} from './billing-auto-top-up-form.schema'
import { BillingCurrencyFormFieldContent } from './billing-currency-form-field.molecule'

export interface BillingAutoTopUpCardProps {
  settings: BillingAutoTopUpSettings
  isPendingEnable: boolean
  onToggle: (isEnabled: boolean) => void
  onSave: (data: { threshold: number; topUpAmount: number }) => void
  className?: string
}

interface AutoTopUpFieldsProps {
  form: UseFormReturn<BillingAutoTopUpFormSchemaType>
}

const AutoTopUpFields: React.FC<AutoTopUpFieldsProps> = ({ form }) => (
  <div className="billing-auto-top-up-fields grid grid-cols-2 gap-2">
    <FormField
      control={form.control}
      name="threshold"
      render={({ field }) => (
        <BillingCurrencyFormFieldContent
          label="Threshold"
          field={field}
          min={MIN_AUTO_TOP_UP_AMOUNT}
          max={MAX_AUTO_TOP_UP_AMOUNT}
          placeholder={String(MIN_AUTO_TOP_UP_AMOUNT)}
          className="billing-auto-top-up-threshold-input"
        />
      )}
    />
    <FormField
      control={form.control}
      name="topUpAmount"
      render={({ field }) => (
        <BillingCurrencyFormFieldContent
          label="Amount"
          field={field}
          min={MIN_AUTO_TOP_UP_AMOUNT}
          max={MAX_AUTO_TOP_UP_AMOUNT}
          placeholder="50"
          className="billing-auto-top-up-amount-input"
        />
      )}
    />
  </div>
)

interface AutoTopUpFormContentProps {
  form: UseFormReturn<BillingAutoTopUpFormSchemaType>
  isPendingEnable: boolean
  onSubmit: (data: BillingAutoTopUpFormSchemaType) => void
}

const AutoTopUpFormContent: React.FC<AutoTopUpFormContentProps> = ({
  form,
  isPendingEnable,
  onSubmit,
}) => {
  const { isDirty, isValid } = form.formState
  const showSaveButton = isPendingEnable || isDirty

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="billing-auto-top-up-form flex flex-col gap-3"
      >
        <AutoTopUpFields form={form} />
        {showSaveButton && (
          <Button
            type="submit"
            disabled={!isValid}
            className="billing-auto-top-up-save-button w-full"
          >
            Save
          </Button>
        )}
      </form>
    </Form>
  )
}

const AutoTopUpDisabledContent: React.FC = () => (
  <p className="text-sm text-muted-foreground">
    Enable to automatically add credits when balance falls below a threshold.
  </p>
)

const BillingAutoTopUpCardBase: React.FC<BillingAutoTopUpCardProps> = ({
  settings,
  isPendingEnable,
  onToggle,
  onSave,
  className,
}): ReactNode => {
  const form = useForm<BillingAutoTopUpFormSchemaType>({
    resolver: zodResolver(billingAutoTopUpFormSchema),
    defaultValues: { threshold: settings.threshold, topUpAmount: settings.topUpAmount },
    mode: 'onChange',
  })

  useEffect(() => {
    form.reset({ threshold: settings.threshold, topUpAmount: settings.topUpAmount })
  }, [settings.threshold, settings.topUpAmount, form])

  const handleSubmit = (data: BillingAutoTopUpFormSchemaType): void => {
    onSave(data)
    form.reset(data)
  }

  return (
    <Card className={cn('billing-auto-top-up-card h-full', className)}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">Auto Top-up</CardTitle>
          <Switch
            checked={settings.isEnabled}
            onCheckedChange={onToggle}
            className="billing-auto-top-up-toggle"
            aria-label="Toggle auto top-up"
          />
        </div>
        <CardDescription>Add credits automatically</CardDescription>
      </CardHeader>
      <CardContent>
        {settings.isEnabled ? (
          <AutoTopUpFormContent
            form={form}
            isPendingEnable={isPendingEnable}
            onSubmit={handleSubmit}
          />
        ) : (
          <AutoTopUpDisabledContent />
        )}
      </CardContent>
    </Card>
  )
}

export const BillingAutoTopUpCard = memo(BillingAutoTopUpCardBase)

BillingAutoTopUpCard.displayName = 'BillingAutoTopUpCard'
